import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import SubscriptionStatus from '../components/SubscriptionStatus';
import UpgradeBanner from '../components/UpgradeBanner';

const formatNaira = (amount) =>
	new Intl.NumberFormat('en-NG', {
		style: 'currency',
		currency: 'NGN',
		maximumFractionDigits: 0,
	}).format(Number(amount || 0));

// Tiny inline SVG bar chart for the 6-month usage history. Bars for events
// created (brand purple) + plates served (accent orange). Each month gets
// a label under the X-axis. Pure SVG so no chart library dep.
const UsageTrendChart = ({ history }) => {
	if (!history || history.length === 0) return null;

	const w = 720;
	const h = 200;
	const padL = 36;
	const padR = 12;
	const padT = 18;
	const padB = 36;
	const plotW = w - padL - padR;
	const plotH = h - padT - padB;

	const eventsMax = Math.max(...history.map((m) => m.eventsCreated || 0), 1);
	const ordersMax = Math.max(...history.map((m) => m.totalOrders || 0), 1);
	const monthW = plotW / history.length;
	const barW = Math.max(12, monthW * 0.35);

	const formatPeriod = (period) => {
		if (!period) return '';
		// UsageCounter.period is "YYYY-MM"
		const [yr, mo] = String(period).split('-');
		if (!yr || !mo) return period;
		const d = new Date(Number(yr), Number(mo) - 1, 1);
		return d.toLocaleString(undefined, { month: 'short' });
	};

	return (
		<div className="ev-top-gap" style={{ overflowX: 'auto' }}>
			<svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="img" aria-label="Monthly usage trend">
				{/* axis line */}
				<line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke="rgba(17,17,19,0.15)" />
				{history.map((m, i) => {
					const cx = padL + monthW * i + monthW / 2;
					const eventsH = ((m.eventsCreated || 0) / eventsMax) * (plotH * 0.92);
					const ordersH = ((m.totalOrders || 0) / ordersMax) * (plotH * 0.92);
					const eventsX = cx - barW - 2;
					const ordersX = cx + 2;
					return (
						<g key={m._id || m.period || i}>
							<rect
								x={eventsX}
								y={padT + plotH - eventsH}
								width={barW}
								height={eventsH || 1}
								fill="#3D1E6D"
								rx={3}
							>
								<title>{`${formatPeriod(m.period)} · ${m.eventsCreated || 0} events`}</title>
							</rect>
							<rect
								x={ordersX}
								y={padT + plotH - ordersH}
								width={barW}
								height={ordersH || 1}
								fill="#FB8601"
								rx={3}
							>
								<title>{`${formatPeriod(m.period)} · ${m.totalOrders || 0} orders`}</title>
							</rect>
							<text
								x={cx}
								y={h - 12}
								textAnchor="middle"
								fontSize="11"
								fill="#6B7280"
								fontFamily="DM Sans, sans-serif"
							>
								{formatPeriod(m.period)}
							</text>
						</g>
					);
				})}
			</svg>
			<div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#6B7280', marginTop: 4 }}>
				<span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#3D1E6D', borderRadius: 2, marginRight: 6, verticalAlign: 'middle' }} /> Events created</span>
				<span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#FB8601', borderRadius: 2, marginRight: 6, verticalAlign: 'middle' }} /> Plates served</span>
			</div>
		</div>
	);
};

const AccountDashboard = () => {
	const [dashboard, setDashboard] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [message, setMessage] = useState('');
	const [cancelling, setCancelling] = useState(false);

	const loadDashboard = async () => {
		setLoading(true);
		try {
			const res = await api.get('/account/dashboard');
			setDashboard(res.data);
		} catch (err) {
			setError(err.response?.data?.msg || 'Failed to load account dashboard.');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadDashboard();
	}, []);

	const handleCancelSubscription = async () => {
		setCancelling(true);
		setError('');
		setMessage('');
		try {
			await api.post('/payments/cancel-subscription');
			setMessage('Subscription will stop auto-renewing at the end of the current period.');
			await loadDashboard();
		} catch (err) {
			setError(err.response?.data?.msg || 'Failed to cancel subscription.');
		} finally {
			setCancelling(false);
		}
	};

	const usage = dashboard?.usage || {};
	const paymentHistory = dashboard?.paymentHistory || [];
	const upgradePrompts = dashboard?.upgradePrompts || [];
	const usageHistory = (dashboard?.usageHistory || []).slice().reverse(); // chronological for chart

	return (
		<div className="ev-user-shell">
			<header className="ev-admin-topbar">
				<div className="ev-admin-topbar-inner">
					<div className="ev-admin-topbar-left">
						<div className="ev-brand-lockup">
							<img src="/images/eventory-logo.svg" alt="Eventory" />
						</div>
					</div>
				</div>
			</header>
			<main className="ev-user-dash">
				<div className="ev-user-wrap">
					<div className="ev-back-row">
						<Link to="/dashboard" className="ev-soft-btn">← My events</Link>
					</div>
					<div className="ev-admin-header">
						<div>
							<h2 className="ev-section-title">Account Dashboard</h2>
							<div className="ev-subtle">Plan, billing, usage, and upgrade suggestions.</div>
						</div>
						<div className="ev-action-group">
							<button type="button" className="ev-soft-btn" onClick={loadDashboard}>↻ Refresh</button>
							<Link to="/pricing" className="ev-primary-btn">View Pricing</Link>
						</div>
					</div>

					{message ? <div className="ev-alert ev-alert-success">{message}</div> : null}
					{error ? <div className="ev-alert ev-alert-danger">{error}</div> : null}
					{upgradePrompts[0] ? <UpgradeBanner message={upgradePrompts[0]} /> : null}

					{!loading ? (
						<>
							<div className="ev-overview-grid">
								<div className="ev-metric-card is-populated">
									<div className="ev-metric-title"><span>Current Plan</span></div>
									<div className="ev-metric-value">{dashboard?.plan?.name || 'Free'}</div>
								</div>
								<div className="ev-metric-card is-populated">
									<div className="ev-metric-title"><span>Events Created</span></div>
									<div className="ev-metric-value">{usage.eventsCreated || 0}</div>
								</div>
								<div className="ev-metric-card is-populated">
									<div className="ev-metric-title"><span>Events Activated</span></div>
									<div className="ev-metric-value">{usage.eventsActivated || 0}</div>
								</div>
								<div className="ev-metric-card is-populated">
									<div className="ev-metric-title"><span>Total Orders</span></div>
									<div className="ev-metric-value">{usage.totalOrders || 0}</div>
								</div>
							</div>

							<SubscriptionStatus
								subscription={dashboard?.subscription}
								onCancel={dashboard?.subscription ? handleCancelSubscription : null}
								cancelling={cancelling}
							/>

							{usageHistory.length >= 2 && (
								<div className="ev-admin-section ev-flat-card">
									<h3 className="ev-zero-margin">6-month trend</h3>
									<div className="ev-subtle" style={{ marginTop: 4 }}>
										Events created and plates served, monthly.
									</div>
									<UsageTrendChart history={usageHistory} />
								</div>
							)}

							<div className="ev-admin-section ev-flat-card">
								<h3 className="ev-zero-margin">Recent Payments</h3>
								{paymentHistory.length === 0 ? (
									<p className="ev-subtle ev-top-gap">No payments yet.</p>
								) : (
									<table className="ev-data-table ev-top-gap">
										<thead>
											<tr>
												<th>Event</th>
												<th>Amount</th>
												<th>Status</th>
												<th>Created</th>
											</tr>
										</thead>
										<tbody>
											{paymentHistory.map((payment) => (
												<tr key={payment._id}>
													<td>{payment.event?.name || 'Event'}</td>
													<td>{formatNaira((payment.amountKobo || 0) / 100)}</td>
													<td>{payment.status}</td>
													<td>{payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : '—'}</td>
												</tr>
											))}
										</tbody>
									</table>
								)}
							</div>
						</>
					) : (
						<div className="ev-empty">Loading account data…</div>
					)}
				</div>
			</main>
		</div>
	);
};

export default AccountDashboard;
